import { postJson } from "./http";

/**
 * Go live: the sequence that turns the onboarded draft into a real tenant.
 *
 * Two verbs. `state` reads where things stand; `run` executes exactly ONE
 * step server-side and answers with the whole state again — the screen keeps
 * calling run while steps keep completing, so a killed request or a closed
 * tab never loses more than the step in flight, and a retry resumes from the
 * failed row (done steps are skipped server-side).
 */

export interface GoLiveStepDTO {
  key: string;
  label: string;
  status: "pending" | "done" | "failed";
  at: string | null;
  detail: string | null;
  error: string | null;
}

/** A client-side dashboard login the live panel shows. */
export interface GoLiveViewerDTO {
  name: string;
  email: string;
}

export interface GoLivePanelDTO {
  /** The tenant admin's login — derived server-side, null until a subdomain exists. */
  admin: { email: string; password: string } | null;
  /**
   * The initial password every dashboard login was created with. A person
   * who already had a Zournal account keeps their own password instead.
   */
  viewer_password: string | null;
  viewers: GoLiveViewerDTO[];
}

export interface GoLiveStateDTO {
  client: {
    public_id: string;
    name: string;
    status: string;
    live_at: string | null;
  };
  subdomain: string;
  app_url: string | null;
  /** True once any step has ever run — the review stage is over. */
  started: boolean;
  /** True when every step is done; the client is live. */
  complete: boolean;
  steps: GoLiveStepDTO[];
  panel: GoLivePanelDTO;
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

const goLiveCall = async (path: string, publicId: string): Promise<GoLiveStateDTO> =>
  (await postJson<Envelope<GoLiveStateDTO>>(path, { public_id: publicId }, ["message"])).data;

/** Where the sequence stands. Touches nothing. */
export const fetchGoLiveState = (publicId: string) =>
  goLiveCall("/api/onboarding/golive/state", publicId);

/**
 * Run the next pending or failed step. A step FAILING is not an error here —
 * the failed row rides back in the state with its sentence on it.
 */
export const runGoLiveStep = (publicId: string) =>
  goLiveCall("/api/onboarding/golive/run", publicId);
